import { useIonModal } from '@ionic/react'
import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'

import { useManagementAssociateUnitOrder, useManagementDisassociateUnitOrder } from '@apps-common/apis/management/order'
import { QUERY_GET_ORDER_DETAILS } from '@apps-common/apis/portal/orders'
import { OrderUnitCard, OrderUnitCardSkeleton } from '@apps-common/apps/portal/pages/orders/pages/details/components'
import { SelectUnitStatusModal } from '@apps-common/apps/portal/pages/orders/pages/details/components/management'
import { PortalOrderUnits } from '@apps-common/apps/portal/pages/orders/pages/details/components/portal'
import { CardContainer, CopyWithText, ImageComponent } from '@apps-common/components/common'
import { SectionTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundedSm, ButtonRoundSm } from '@apps-common/components/elements'
import { VertSpacerLg } from '@apps-common/components/layout'
import { ModalButton, PortalUnitSelectModal } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { OrderUnitType } from '@apps-common/config/orders'
import { UNIT_STATUS_ACTIVE, UNIT_STATUS_INACTIVE } from '@apps-common/config/unit-filter'
import { PORTAL_UNIT_FILER_DEFAULTS_ACTIVE, PORTAL_UNIT_FILER_DEFAULTS_INACTIVE } from '@apps-common/config/unit-filter-defaults'
import { sortAlphabetically } from '@apps-common/helpers/array-sort'
import { checkAccountNo } from '@apps-common/helpers/unit-helpers'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { queryClient } from '@apps-common/lib'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { toastWarning } from '@apps-common/lib/toast'
import { IOrderDetailsModel, IOrderUnitModel } from '@apps-common/types/portal/order'
import { IPortalUnitSearchModel } from '@apps-common/types/portal/unit'
import { ROUTE_UNIT_MANAGE } from '@apps-radionet-portal-root/src/config/private-routes'

interface IProps {
  data?: IOrderDetailsModel
  loading: boolean
  updating: boolean
  classes?: string
}

interface IUnitRequirements {
  requiredUnits: {
    orderLineId: string
    productId: string
    productName: string
    requiredCount: number
    actionRequired: boolean
    unitsRemaining: number
    existingOrderLineUnits: IOrderUnitModel[]
    newOrderLineUnits: IPortalUnitSearchModel[]
  }[]
  requiredUnitsCount: number
  currentUnitsCount: number
  requiresAction: boolean
}

export const ManagementOrderUnits: React.FC<IProps> = ({ data, loading, updating, classes }) => {
  const { order, orderLine, units } = data || {}
  const { navigateForward } = useNavigation()

  const [unitSelectProps, setUnitSelectProps] = useState<any>()

  const [present, dismiss] = useIonModal(PortalUnitSelectModal, {
    componentProps: unitSelectProps,
    onDismiss: (d: string, role: string) => {
      dismiss(d, role)
    },
    enableBackdropDismiss: true
  })

  const [unitRequirements, setUnitRequirements] = useState<IUnitRequirements>()

  const addUnitToOrderLine = (unit: IPortalUnitSearchModel, orderLineId: string) => {
    setUnitRequirements((prev) => {
      if (!prev) return undefined

      const olItem = prev?.requiredUnits?.find((ol) => ol.orderLineId === orderLineId)

      if (!olItem) {
        toastWarning('Order line not found')

        return prev
      }

      if (olItem.requiredCount <= (olItem.existingOrderLineUnits?.length || 0) + (olItem.newOrderLineUnits?.length || 0)) {
        toastWarning('Order line already has enough devices')

        return prev
      }

      const allUnits = prev?.requiredUnits?.reduce(
        (acc: string[], curr) => [
          ...(curr?.existingOrderLineUnits || []).map((u) => u.unitId),
          ...(curr?.newOrderLineUnits || []).map((u) => u.unitId),
          ...acc
        ],
        []
      )

      const unitAlreadyInExists = allUnits.find((u) => u === unit.unitId)

      if (unitAlreadyInExists) {
        toastWarning('This device is already associated with this order')

        return prev
      }

      olItem?.newOrderLineUnits?.push(unit)

      const cloned = [...(prev?.requiredUnits || [])]
      const olItemIndex = cloned.findIndex((ol) => ol.orderLineId === orderLineId)

      cloned[olItemIndex] = olItem!

      return { ...prev, requiredUnits: cloned }
    })
  }

  const removeNewUnitFromOrderLine = (unitId: string) => {
    setUnitRequirements((prev) => {
      if (!prev) return undefined

      return {
        ...prev,
        requiredUnits: prev.requiredUnits.map((ru) => ({
          ...ru,
          newOrderLineUnits: ru.newOrderLineUnits.filter((nolu) => nolu.unitId !== unitId)
        }))
      }
    })
  }

  useEffect(() => {
    const requiredUnits = (orderLine || [])
      .filter((ol) => ol.productActionRequired)
      .map((ol) => {
        const orderLineUnits = (units || []).filter((u) => u.orderLineId === ol.orderLineId)

        return {
          orderLineId: ol.orderLineId,
          productId: ol.productId,
          productName: ol.productName,
          requiredCount: ol.productQuantity,
          actionRequired: orderLineUnits.length < ol.productQuantity,
          unitsRemaining: ol.productQuantity - orderLineUnits.length,
          existingOrderLineUnits: orderLineUnits,
          newOrderLineUnits: []
        }
      })

    const requiredUnitsCount = requiredUnits.reduce((acc, curr) => (curr?.requiredCount || 0) + acc, 0)
    const currentUnitsCount = units?.length || 0

    setUnitRequirements({
      requiredUnits,
      requiredUnitsCount,
      currentUnitsCount,
      requiresAction: currentUnitsCount < requiredUnits.reduce((acc, curr) => (curr?.requiredCount || 0) + acc, 0)
    })
  }, [units])

  const updatedUnitRequirements = useMemo(
    () => ({
      ...unitRequirements,
      requiredUnits: unitRequirements?.requiredUnits.map((ru) => ({
        ...ru,
        unitsRemaining: ru.requiredCount - ((ru.existingOrderLineUnits?.length || 0) + (ru.newOrderLineUnits?.length || 0))
      }))
    }),
    [units, unitRequirements]
  )

  const hasNewUnitsPendingSave = useMemo(
    () => (unitRequirements?.requiredUnits?.reduce((acc, curr) => acc + (curr?.newOrderLineUnits || []).length, 0) || 0) > 0,
    [unitRequirements]
  )

  const { isPending: isAddingOrderUnits, mutate: addOrderUnits } = useManagementAssociateUnitOrder(true, () => {
    queryClient.refetchQueries({ queryKey: QUERY_GET_ORDER_DETAILS(order?.orderId || '') })
  })

  const { isPending: isRemovingOrderUnit, mutate: removeOrderUnit } = useManagementDisassociateUnitOrder(true, () => {
    queryClient.refetchQueries({ queryKey: QUERY_GET_ORDER_DETAILS(order?.orderId || '') })
  })

  if (!updatedUnitRequirements || !updatedUnitRequirements?.requiresAction) {
    return <PortalOrderUnits data={data} loading={loading} updating={updating} classes={classes} />
  }

  const isUpdating = updating || isAddingOrderUnits || isRemovingOrderUnit

  return (
    <CardContainer bgClasses={classNames('lg:absolute bg-surface overflow-hidden !h-[100%] flex flex-col !pb-0', classes)}>
      <div className='absolute z-[10] top-0 left-0 px-6 py-4 w-full rounded-t-3xl backdrop-blur-md flex items-center justify-between'>
        <SectionTitle classes='text-text' text='Devices' />
        {updatedUnitRequirements.requiresAction && (
          <button
            type='button'
            disabled={loading || isUpdating || !hasNewUnitsPendingSave}
            className='jost-light text-[0.9rem] action-brand-solid disabled:opacity-60 rounded-full flex items-center justify-center gap-[5px] px-[10px] py-[2px]'
            onClick={() => {
              const newUnits: any =
                unitRequirements?.requiredUnits?.reduce(
                  (acc, curr) =>
                    [...(curr?.newOrderLineUnits || []).map((nu) => ({ unitId: nu.unitId, orderLineId: curr.orderLineId })), ...acc] as any,
                  []
                ) || []

              if (!newUnits || newUnits?.length === 0) {
                toastWarning('No devices selected')

                return
              }

              addOrderUnits({
                model: {
                  orderId: order?.orderId || '',
                  units: newUnits
                }
              })
            }}
          >
            Save
          </button>
        )}
      </div>
      {loading && (
        <div className='grid xl:grid-cols-2 gap-[20px] overflow-y-auto overflow-x-hidden scrollbar-none pt-[40px] pb-6'>
          {Array.from(Array(8).keys()).map((k) => (
            <div key={`order-unit-skeleton-${k}`}>
              <OrderUnitCardSkeleton />
            </div>
          ))}
        </div>
      )}
      {!loading && (
        <>
          {updatedUnitRequirements.requiresAction && (
            <div className='flex flex-col gap-[20px] grow pt-[40px] pb-6 overflow-y-auto scrollbar-none'>
              {updatedUnitRequirements?.requiredUnits?.map((ru, i) => {
                const countRemaining = ru.requiredCount - (ru.existingOrderLineUnits?.length || 0) - (ru.newOrderLineUnits?.length || 0)
                const requiresMore = countRemaining > 0

                return (
                  <div key={`order-unit-add-${ru?.productId}-${i}`} className='flex flex-col gap-[10px]'>
                    <div className='text-[0.8rem] bg-surface-2 text-text flex items-center justify-between gap-[5px] rounded-xl px-4 py-2'>
                      <span>
                        {ru?.requiredCount} x {ru?.productName}
                      </span>
                      {requiresMore && (
                        <div className='flex items-center justify-end gap-[5px]'>
                          <div className='bg-warning text-on-warning rounded-full px-3'>+{countRemaining} more</div>
                        </div>
                      )}
                      {!requiresMore && (
                        <div className='bg-success border border-on-success rounded-full'>
                          <commonIcons.check className='text-on-success text-[1.2rem]' />
                        </div>
                      )}
                    </div>
                    <div className='grid md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-[20px] overflow-y-auto overflow-x-hidden scrollbar-none'>
                      {ru?.existingOrderLineUnits?.map((eu, eui) => (
                        <OrderUnitCard
                          index={eui}
                          loading={loading}
                          updating={updating}
                          unit={eu}
                          removeCb={(unitId: string) =>
                            removeOrderUnit({ model: { orderId: order?.orderId || '', orderLineId: ru.orderLineId, unitId } })
                          }
                        />
                      ))}
                      {ru?.newOrderLineUnits?.map((nu, nui) => (
                        <OrderUnitCard
                          index={nui}
                          loading={loading}
                          updating={updating}
                          unit={nu}
                          removeCb={(unitId: string) => removeNewUnitFromOrderLine(unitId)}
                        />
                      ))}
                    </div>
                    {requiresMore && (
                      <ModalButton
                        component={SelectUnitStatusModal}
                        disabled={false}
                        componentProps={{}}
                        confirmCallback={(opt) => {
                          if (!opt || opt?.length === 0) return

                          if (opt === OrderUnitType.Create) {
                            console.log('open create unit modal')
                          } else {
                            const exclusions = {
                              ids: [
                                ...(ru?.existingOrderLineUnits || []).map((eu) => eu.unitId),
                                ...(ru?.newOrderLineUnits || []).map((nu) => nu.unitId)
                              ],
                              component: (
                                <div className='w-full text-[0.85em] bg-warning text-on-warning flex items-center justify-between grow px-[10px] py-[5px] rounded-full'>
                                  Device already added <commonIcons.toastWarning className='text-[1.4em]' />
                                </div>
                              )
                            }

                            if (opt === OrderUnitType.Active) {
                              setUnitSelectProps({
                                title: 'Active Devices',
                                exclusions,
                                filterDefaults: PORTAL_UNIT_FILER_DEFAULTS_ACTIVE
                              })
                            } else {
                              setUnitSelectProps({
                                title: 'Inactive Devices',
                                exclusions,
                                filterDefaults: PORTAL_UNIT_FILER_DEFAULTS_INACTIVE
                              })
                            }

                            present({
                              enterAnimation: modalSlideUpEnterAnimation,
                              leaveAnimation: modalSlideUpLeaveAnimation,
                              backdropDismiss: true,
                              onDidDismiss: (d: any) => {
                                let item

                                try {
                                  item = JSON.parse(d?.detail?.data)
                                } catch {
                                  item = undefined
                                }

                                if (item) {
                                  addUnitToOrderLine(item, ru?.orderLineId)
                                }
                              }
                            })
                          }
                        }}
                        enterAnimation={modalSlideUpEnterAnimation}
                        leaveAnimation={modalSlideUpLeaveAnimation}
                        backdropDismiss
                        classes='mx-auto my-4'
                      >
                        <ButtonRoundedSm
                          type='div'
                          loading={false}
                          text='Add device'
                          className='sm:grow-0 action-success'
                          icon={<commonIcons.add />}
                        />
                      </ModalButton>
                    )}
                  </div>
                )
              })}
            </div>
          )}
          {!updatedUnitRequirements.requiresAction && units && units?.length === 0 && (
            <div className='flex flex-col gap-[20px] grow pt-[40px] pb-6'>
              {(!units || units?.length === 0) && (
                <div className='flex flex-col items-center justify-center gap-[5px] bg-secondary-content/5 text-secondary-content/70 rounded-3xl p-6'>
                  <div className='jost-medium'>No devices</div>
                  <div>There are no devices associated with this order</div>
                </div>
              )}
            </div>
          )}
          {!updatedUnitRequirements.requiresAction && units && units?.length > 0 && (
            <div className='grid xl:grid-cols-2 gap-[20px] overflow-y-auto overflow-x-hidden scrollbar-none pt-[40px] pb-6'>
              {units &&
                units?.length > 0 &&
                units
                  .sort((a, b) => sortAlphabetically(a, b, 'status'))
                  .map((u, i) => {
                    const { unitId, status, accountNo, serialNo, imei, mobileNo, simChanged, productImageUrl } = u

                    return (
                      <div
                        role='button'
                        tabIndex={i}
                        key={`order-unit-${u.unitId}-${i}`}
                        className='relative bg-secondary-content/5 text-secondary-content rounded-3xl p-[20px]'
                        onClick={() => {
                          if (!loading && !updating) {
                            navigateForward({ routeUrl: ROUTE_UNIT_MANAGE.buildPath(status, unitId), route: ROUTE_UNIT_MANAGE })
                          }
                        }}
                      >
                        <div className='flex items-center justify-between gap-[20px]'>
                          <div className='flex flex-col gap-[10px]'>
                            <div className=' flex items-center justify-start gap-[5px]'>
                              <div
                                className={classNames(
                                  'absolute top-[12px] inline-block rounded-full text-[0.7rem] leading-[0.7rem] jost-medium px-[7px] py-[2px]',
                                  status === UNIT_STATUS_INACTIVE.value && 'bg-pending text-pending-content',
                                  status === UNIT_STATUS_ACTIVE.value && 'bg-success text-success-content'
                                )}
                              >
                                {status}
                              </div>
                              {simChanged && (
                                <div className='flex items-center justify-center gap-[5px] bg-warning text-warning-content rounded-full jost-medium text-[0.8rem] leading-[0.8rem] px-[10px] py-[5px]'>
                                  sim <commonIcons.warning className='text-[0.8rem]' />
                                </div>
                              )}
                            </div>
                            <div className='flex items-center justify-start gap-[5px]'>
                              <div className='px-4 py-2 h-[20px] rounded-[15px] flex items-center justify-start gap-[10px] min-w-0 bg-secondary-content/10'>
                                <commonIcons.processor className='text-secondary-content' />
                                <div className={classNames('jost-light text-[0.85rem] uppercase')}>{checkAccountNo(accountNo, status)}</div>
                              </div>
                            </div>
                          </div>
                          <div
                            className={classNames(
                              'w-[45px] h-[45px] rounded-full flex items-center justify-center overflow-hidden bg-secondary-content/10'
                            )}
                          >
                            <ImageComponent
                              src={productImageUrl}
                              alt={`Product ${accountNo}`}
                              imageClasses={classNames('max-w-[30px]')}
                              errorIconClasses='text-[1.2rem]'
                            />
                          </div>
                        </div>
                        <VertSpacerLg />
                        <div className='flex items-end justify-between gap-[5px]'>
                          <div className='grow'>
                            {status === UNIT_STATUS_INACTIVE.value && (
                              <>
                                <CopyWithText textValue='SN' value={serialNo} showCopyValue className={classNames('!text-[0.75rem]')} />
                                <CopyWithText textValue='IMEI' value={imei} showCopyValue className={classNames('!text-[0.75rem]')} />
                              </>
                            )}
                            {status === UNIT_STATUS_ACTIVE.value && (
                              <>
                                <CopyWithText
                                  textValue='MOB'
                                  value={mobileNo}
                                  showCopyValue
                                  className={classNames('grow !text-[0.75rem]')}
                                />
                                <CopyWithText textValue='IMEI' value={imei} showCopyValue className={classNames('!text-[0.75rem]')} />
                              </>
                            )}
                          </div>
                          <ButtonRoundSm
                            type='button'
                            className='bg-secondary-content/5'
                            disabled={loading || updating}
                            tooltip={{
                              id: `manage-order-unit-${unitId}-${i}`,
                              message: 'Manage device'
                            }}
                          >
                            <commonIcons.arrowAngled className='text-[1rem] text-secondary-content/70' />
                          </ButtonRoundSm>
                        </div>
                      </div>
                    )
                  })}
            </div>
          )}
        </>
      )}
    </CardContainer>
  )
}
